import { api } from '@/lib/api'

import { bucketsToCommonValues } from './transforms/response'
import { composeCommonValuesBody } from './transforms/search-body'

import type { CommonValueEntry } from './transforms/response'
import type { ComposeCommonValuesInput } from './transforms/search-body'

type CommonValuesResponse = {
	aggregations?: {
		display_names?: {
			buckets: Array<{ key: string; doc_count: number }>
			sum_other_doc_count?: number
		}
	}
}

/**
 * Parameters for {@link fetchFieldCommonValues}. The optional `tr`/`ft`/`q`
 * scope the counts to a search context (Search passes them; custom policies,
 * which target a single source with no temporal/filter context, do not).
 */
export type FetchFieldCommonValuesParams = Pick<
	ComposeCommonValuesInput,
	'field' | 'tr' | 'ft' | 'q' | 'size'
> & {
	/** `[definitionId, eventType]` pairs to query. */
	sources: Array<[string, string]>
}

/**
 * Fetch the most common values of a single field, scoped to one or more
 * `[definitionId, eventType]` sources. The single network builder behind every
 * common-values request (Search popover + suggestions, custom-policy
 * suggestions) — same endpoint, same `composeCommonValuesBody`.
 *
 * POST — never pass an abort signal (cancelling a POST yields neither result
 * nor error).
 *
 * @param tenant - The tenant ID.
 * @param params - Field, sources, size, and optional search context.
 * @returns `{value, count, percent}` rows.
 */
export async function fetchFieldCommonValues(
	tenant: string,
	{ field, sources, size = 15, tr, ft, q }: FetchFieldCommonValuesParams,
): Promise<Array<CommonValueEntry>> {
	const body = {
		query: composeCommonValuesBody({ field, size, tr, ft, q }),
		sources,
	}

	const { data } = await api.post<CommonValuesResponse>(
		`/api/tenants/${tenant}/logs/search`,
		body,
	)

	return bucketsToCommonValues(data.aggregations?.display_names)
}
